import {
    Autocomplete,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    FormGroup,
    Step,
    StepLabel,
    Stepper,
    TextField,
} from "@mui/material";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Localized, useLocalization } from "@fluent/react";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { selectAllCleaningRooms, selectCleaningRoomById } from "./cleaningRoomsSlice";
import { selectAllSensors } from "./sensorsSlice";
import { addMeasurementHeader } from "./measurementHeadersSlice";
import { Measurement } from "./measurementsApi";
import { MeasurmentType } from "./types";

const MEASUREMENT_TYPES: MeasurmentType[] = [
    'particles-in-air',
    'air-exchange-rate',
    'pressure-difference',
    'temp-and-humidity',
    'light-intensity',
    'HEPA-integrity-and-tightness',
];

const LOCATION_COUNT = 6;

interface Props {
    open: boolean
    onClose: () => void
}

export const CreateMeasurement = ({ open, onClose }: Props) => {
    const { l10n } = useLocalization();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const cleaningRooms = useAppSelector(selectAllCleaningRooms);
    const allSensors = useAppSelector(selectAllSensors);

    const [activeStep, setActiveStep] = useState(0);
    const [name, setName] = useState('');
    const [cleaningRoomId, setCleaningRoomId] = useState('');
    const [measurementTypes, setMeasurementTypes] = useState<MeasurmentType[]>([]);
    const [sensorIds, setSensorIds] = useState<Partial<Record<MeasurmentType, string>>>({});

    const room = useAppSelector(state => cleaningRoomId ? selectCleaningRoomById(state, cleaningRoomId) : undefined);

    const steps = [
        l10n.getString('measurement-step-name', null, 'Name'),
        l10n.getString('measurement-step-cleaning-room', null, 'Cleaning Room'),
        l10n.getString('measurement-step-type', null, 'Type'),
        l10n.getString('measurement-step-sensors', null, 'Sensors'),
    ];

    const isStepValid = () => {
        switch (activeStep) {
            case 0: return name.trim().length > 0;
            case 1: return cleaningRoomId !== '';
            case 2: return measurementTypes.length > 0;
            case 3: return measurementTypes.every(t => Boolean(sensorIds[t]));
            default: return false;
        }
    };

    const handleNext = () => setActiveStep(s => s + 1);
    const handleBack = () => setActiveStep(s => s - 1);

    const handleFinish = () => {
        const id = crypto.randomUUID();
        const measurements: Partial<Record<MeasurmentType, Measurement>> = {};
        for (const type of measurementTypes) {
            measurements[type] = {
                id: crypto.randomUUID(),
                type,
                sensorId: sensorIds[type]!,
                date: new Date().toISOString().split('T')[0],
                roomDrawing: room?.image ?? null,
                locations: Array.from({ length: LOCATION_COUNT }, (_, i) => ({
                    id: crypto.randomUUID(),
                    code: String(i + 1).padStart(3, '0'),
                    remarks: '',
                    measuredValue: null,
                    referenceValue: null,
                    minValue: null,
                    maxValue: null,
                    unit: null,
                    x: null,
                    y: null,
                    concentration05um: null,
                    concentration5um: null,
                })),
                methodology: '',
                tolerance: 0,
            };
        }
        dispatch(addMeasurementHeader({ id, name, clearRoomId: cleaningRoomId, measurements }));
        handleClose();
        navigate(`/measurements/edit/${id}`);
    };

    const handleClose = () => {
        setActiveStep(0);
        setName('');
        setCleaningRoomId('');
        setMeasurementTypes([]);
        setSensorIds({});
        onClose();
    };

    const renderStepContent = () => {
        switch (activeStep) {
            case 0:
                return (
                    <TextField
                        autoFocus
                        fullWidth
                        label={<Localized id="measurement-step-name"><span>Name</span></Localized>}
                        value={name}
                        onChange={e => setName(e.target.value)}
                        sx={{ mt: 2 }}
                    />
                );
            case 1:
                return (
                    <Autocomplete
                        sx={{ mt: 2 }}
                        options={cleaningRooms}
                        getOptionLabel={r => `${r.number} – ${r.name}`}
                        value={cleaningRooms.find(r => r.id === cleaningRoomId) ?? null}
                        onChange={(_, val) => setCleaningRoomId(val?.id ?? '')}
                        renderInput={params => (
                            <TextField {...params} label={<Localized id="measurement-step-cleaning-room"><span>Cleaning Room</span></Localized>} />
                        )}
                    />
                );
            case 2:
                return (
                    <FormControl fullWidth sx={{ mt: 2 }}>
                        <FormGroup>
                            {MEASUREMENT_TYPES.map(type => (
                                <FormControlLabel
                                    key={type}
                                    control={
                                        <Checkbox
                                            checked={measurementTypes.includes(type)}
                                            onChange={e => {
                                                setMeasurementTypes(prev =>
                                                    e.target.checked
                                                        ? [...prev, type]
                                                        : prev.filter(t => t !== type)
                                                );
                                                if (!e.target.checked) {
                                                    setSensorIds(prev => {
                                                        const next = { ...prev };
                                                        delete next[type];
                                                        return next;
                                                    });
                                                }
                                            }}
                                        />
                                    }
                                    label={l10n.getString(`measurment-type-${type}`, null, type)}
                                />
                            ))}
                        </FormGroup>
                    </FormControl>
                );
            case 3:
                return (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                        {measurementTypes.map(type => {
                            const filteredSensors = allSensors.filter(s => s.measurmentTypes.includes(type));
                            const selectedSensor = filteredSensors.find(s => s.id === sensorIds[type]) ?? null;
                            return (
                                <Autocomplete
                                    key={type}
                                    options={filteredSensors}
                                    getOptionLabel={s => s.name}
                                    value={selectedSensor}
                                    onChange={(_, sensor) =>
                                        setSensorIds(prev => ({ ...prev, [type]: sensor?.id ?? undefined }))
                                    }
                                    renderInput={params => (
                                        <TextField
                                            {...params}
                                            label={l10n.getString(`measurment-type-${type}`, null, type)}
                                            required
                                        />
                                    )}
                                />
                            );
                        })}
                    </Box>
                );
            default:
                return null;
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md" PaperProps={{ sx: { height: 600 } }}>
            <DialogTitle>
                <Localized id="create-measurement"><span>Create measurement</span></Localized>
            </DialogTitle>
            <DialogContent>
                <Stepper activeStep={activeStep}>
                    {steps.map(label => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
                {renderStepContent()}
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'space-between' }}>
                <Button onClick={activeStep === 0 ? handleClose : handleBack}>
                    {activeStep === 0
                        ? <Localized id="cancel"><span>Cancel</span></Localized>
                        : <Localized id="back"><span>Back</span></Localized>
                    }
                </Button>
                {activeStep < steps.length - 1
                    ? (
                        <Button variant="contained" onClick={handleNext} disabled={!isStepValid()}>
                            <Localized id="next"><span>Next</span></Localized>
                        </Button>
                    )
                    : (
                        <Button variant="contained" onClick={handleFinish} disabled={!isStepValid()}>
                            <Localized id="finish"><span>Finish</span></Localized>
                        </Button>
                    )
                }
            </DialogActions>
        </Dialog>
    );
};
